// client/src/components/plataforma/franquias/FranquiasPage.js
import React, { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFranquias } from '../../../hooks/useFranquias';
import { useAuth } from '../../../context/auth/AuthProvider'; // Novo hook de autenticação
import { useAuthMutations } from '../../../hooks/useAuth';
import { isMatrizAdmin } from '../../../utils/permissions';
import useAlert from '../../../hooks/useAlert';
import { Plus, ShieldCheck, Edit, Loader2, LogIn, Building2, UserCog, MoreVertical } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';

const FranquiaRow = ({ franquia, onOpenActions }) => {
    return (
        <tr onClick={() => onOpenActions(franquia)} style={{ cursor: 'pointer' }}>
            <td className="td-name align-middle">
                <div className="table-icon-wrapper icon-pj">
                    <ShieldCheck size={14} />
                </div>
                {franquia.nome}
            </td>
            <td className="align-middle">{franquia.cidade}/{franquia.estado}</td>
            <td className="align-middle">{franquia.responsavel?.name || 'Não definido'}</td>
            <td className="align-middle">
                <span className={`badge ${
                    franquia.status === 'trialing' ? 'badge-warning' :
                    (franquia.status === 'active' ? 'badge-success' : 'badge-danger')
                }`}>
                    {franquia.status}
                </span>
            </td>
            <td className="td-actions text-right whitespace-nowrap align-middle">
                <button
                    onClick={(e) => { e.stopPropagation(); onOpenActions(franquia); }}
                    title="Ações"
                    className="action-button-icon text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <MoreVertical size={18} />
                </button>
            </td>
        </tr>
    );
};

const FranquiasPage = () => {
    const { data: franquias = [], isLoading: loadingFranquias } = useFranquias();
    const navigate = useNavigate();
    const { user, isLoading: authLoading } = useAuth(); // Usando o novo hook
    const { setAlert } = useAlert();
    const { impersonateUser, isLoading: isImpersonating } = useAuthMutations();
    const [selectedFranquia, setSelectedFranquia] = useState(null);

    const isLoading = loadingFranquias || authLoading;
    const hasFranquias = franquias.length > 0;

    // ✅ CORREÇÃO: GET /api/v1/franquias devolve responsavel.id (toJSON do
    // Mongoose expõe "id", não "_id" — confirmado no payload real da API),
    // mas o código só checava "._id" — undefined sempre, então a opção
    // "Logar como" nunca aparecia no menu de ações, mesmo pra franquias com
    // responsável ativo. Aceita os dois formatos por segurança.
    const getResponsavelId = (franquia) => franquia?.responsavel?.id || franquia?.responsavel?._id;

    const handleImpersonateClick = (franquia) => {
        const responsavelId = getResponsavelId(franquia);
        if (!responsavelId) {
            setAlert('Esta franquia não possui um administrador responsável para personificar.', 'warning');
            return;
        }
        if (window.confirm(`Você tem certeza que deseja logar como ${franquia.responsavel.name}, o administrador da franquia "${franquia.nome}"?`)) {
            impersonateUser.mutate({ userId: responsavelId, franquiaId: franquia._id });
        }
        setSelectedFranquia(null);
    };

    const actions = selectedFranquia ? [
        ...(isMatrizAdmin(user) && getResponsavelId(selectedFranquia) ? [{
            key: 'login',
            label: `Logar como ${selectedFranquia.responsavel?.name}`,
            icon: <LogIn size={20} className="icon" />,
            onClick: () => handleImpersonateClick(selectedFranquia),
            loading: isImpersonating,
        }] : []),
        {
            key: 'contas',
            label: 'Ver Contas da Franquia',
            icon: <Building2 size={20} className="icon" />,
            onClick: () => { navigate(`/franquias/${selectedFranquia._id}/contas`); setSelectedFranquia(null); },
        },
        {
            key: 'admins',
            label: 'Ver Admins da Franquia',
            icon: <UserCog size={20} className="icon" />,
            onClick: () => { navigate(`/franquias/${selectedFranquia._id}/admins`); setSelectedFranquia(null); },
        },
        {
            key: 'editar',
            label: 'Editar Franquia',
            icon: <Edit size={20} className="icon" />,
            onClick: () => { navigate(`/franquias/${selectedFranquia._id}/editar`); setSelectedFranquia(null); },
        },
    ] : [];

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<ShieldCheck />}
                    title="Gestão de Franquias"
                    subtitle={`${franquias.length} franquia(s)`}
                />
            </div>

            <div className="table-container">
                <div className="table-wrapper">
                    {isLoading ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin" />
                            <span>Carregando franquias...</span>
                        </div>
                    ) : hasFranquias ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th className="text-left align-middle">Nome da Franquia</th>
                                    <th className="text-left align-middle">Cidade/Estado</th>
                                    <th className="text-left align-middle">Responsável</th>
                                    <th className="text-left align-middle">Status</th>
                                    <th className="text-right align-middle">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {franquias.map((franquia) => (
                                    <FranquiaRow key={franquia._id} franquia={franquia} onOpenActions={setSelectedFranquia} />
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <ShieldCheck size={48} className="text-gray-400 mx-auto" />
                            <h3 className="text-lg font-semibold text-gray-700 mt-4 mb-2">Nenhuma franquia cadastrada</h3>
                            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                                Cadastre a primeira franquia da plataforma.
                            </p>
                            <button
                                onClick={() => navigate('/franquias/novo')}
                                className="action-button action-button-primary"
                            >
                                <Plus size={18} className="icon" /> Nova Franquia
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ActionsDrawer
                open={!!selectedFranquia}
                onClose={() => setSelectedFranquia(null)}
                title={selectedFranquia?.nome || 'Franquia'}
                subtitle={selectedFranquia ? `${selectedFranquia.cidade}/${selectedFranquia.estado}` : null}
                actions={actions}
            />

            <FabButton
                onClick={() => navigate('/franquias/novo')}
                title="Nova Franquia"
                visible={true}
                icon={<ShieldCheck size={26} className="icon" />}
            />
        </Fragment>
    );
};

export default FranquiasPage;
